import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import * as blueprint from '@adc/blueprint';
import * as graph from '@adc/sdk';
import classnames from 'classnames/bind';
import { Grid } from 'semantic-ui-react';
import { GRAPH_API_FRAGMENT_DEFINITION, GRAPH_API_FRAGMENT_REFERENCE, } from '../../../utils/symbols';
import ListingBadge, { BADGE_MODE } from './definitions/base';
import { ShortSaleBadge, ShortSaleOpenHousesAvailableBadge, } from './definitions/shortSale';
import RentalBadge from './definitions/rental';
import FirstLookBadge from './definitions/firstLook';
import { BrokerCoopAvailableBadge, BrokerCoopUnavailableBadge, } from './definitions/brokerCoop';
import { PropertyOccupiedBadge, PropertyUnknownOccupancyBadge, PropertyVacantBadge, } from './definitions/occupancy';
import { BuyerPremiumAvailableBadge, BuyerPremiumUnavailableBadge, } from './definitions/buyerPremium';
import { CashOnlyBadge, FinancingAvailableBadge } from './definitions/cashOnly';
import { InteriorAccessAvailableBadge, InteriorAccessAvailableNoAgentBadge, InteriorAccessUnavailableBadge, } from './definitions/interiorAccess';
import { ClearedForSalePostponedBadge, ClearedForSalePendingBadge, ClearedForSaleComingSoonBadge, ClearedForSaleYesBadge, } from './definitions/clearedForSale';
import { NotConductedByADCFCLBadge, ConductedByADCOnlineBadge, ConductedByADCLiveBadge, NotConductedByADCLive, } from './definitions/conductedByADC';
import AgentInformationBadge from './definitions/agentInformation';
import OpenHouseScheduledBadge from './definitions/openHouse';
import SubjectToRedemptionBadge from './definitions/redemption';
import ForeclosureContactInformationBadge from './definitions/foreclosureContact';
import TitleAndLiensBadge from './definitions/titleAndLiens';
import RemoteBidBadge from './definitions/remoteBid';
import NewlyListedBadge from './definitions/newlyListed';
import cls from './styles.scss';
import NewlyVacantBadge from './definitions/newlyVacant';
import PriceDroppedBadge from './definitions/priceDropped';
import HotBadge from './definitions/hot';
import LikelyToGoToSaleBadge from './definitions/likelyToGoToSale';
import { FinanceContingency, InspectionContigency, } from './definitions/contingency';
import { RentbackAuction, RentbackOffer } from './definitions/rentback';
import { OptionFee } from './definitions/optionFee';
import { AdditionalTermsBadge } from './definitions/additionalTerms';
import { WinningBidDepositBadge } from './definitions/winningBidDeposit';
const cx = classnames.bind(cls);
const availableBadges = [
    PropertyOccupiedBadge,
    PropertyUnknownOccupancyBadge,
    PropertyVacantBadge,
    ShortSaleBadge,
    ShortSaleOpenHousesAvailableBadge,
    InteriorAccessAvailableBadge,
    InteriorAccessAvailableNoAgentBadge,
    InteriorAccessUnavailableBadge,
    BuyerPremiumAvailableBadge,
    BuyerPremiumUnavailableBadge,
    BrokerCoopAvailableBadge,
    BrokerCoopUnavailableBadge,
    FinancingAvailableBadge,
    CashOnlyBadge,
    FirstLookBadge,
    OpenHouseScheduledBadge,
    SubjectToRedemptionBadge,
    RentalBadge,
    TitleAndLiensBadge,
    AgentInformationBadge,
    ConductedByADCOnlineBadge,
    ConductedByADCLiveBadge,
    NotConductedByADCFCLBadge,
    NotConductedByADCLive,
    ClearedForSalePostponedBadge,
    ClearedForSalePendingBadge,
    ClearedForSaleComingSoonBadge,
    ClearedForSaleYesBadge,
    RemoteBidBadge,
    NewlyListedBadge,
    ForeclosureContactInformationBadge,
];
const availableBadgesV2 = [
    NewlyVacantBadge,
    PriceDroppedBadge,
    NewlyListedBadge,
    FirstLookBadge,
    InteriorAccessAvailableBadge,
    InteriorAccessAvailableNoAgentBadge,
    ClearedForSaleYesBadge,
    HotBadge,
    LikelyToGoToSaleBadge,
];
const customMarkupContentBadges = {
    AgentInformationBadge,
    BrokerCoopUnavailableBadge,
    CashOnlyBadge,
    ClearedForSalePendingBadge,
    ConductedByADCOnlineBadge,
    ConductedByADCLiveBadge,
    NotConductedByADCLive,
    NotConductedByADCFCLBadge,
    ForeclosureContactInformationBadge,
    InteriorAccessAvailableBadge,
    InteriorAccessAvailableNoAgentBadge,
    PropertyOccupiedBadge,
    OpenHouseScheduledBadge,
    SubjectToRedemptionBadge,
    RentalBadge,
    ShortSaleBadge,
    ShortSaleOpenHousesAvailableBadge,
    FinanceContingency,
    InspectionContigency,
    RentbackOffer,
    RentbackAuction,
    OptionFee,
    AdditionalTermsBadge,
    TitleAndLiensBadge,
    WinningBidDepositBadge,
};
const getBadgeContent = (badge, listing, eventSubscriber) => {
    if (Object.keys(customMarkupContentBadges).includes(badge.id)) {
        const content = new customMarkupContentBadges[badge.id]({
            listing,
            eventSubscriber,
        }).content;
        return content || badge.content;
    }
    return badge.content;
};
const getBadgeTooltip = (badge, listing, eventSubscriber) => {
    if (Object.keys(customMarkupContentBadges).includes(badge.id)) {
        const tooltipContent = new customMarkupContentBadges[badge.id]({
            listing,
            eventSubscriber,
        }).tooltipContent;
        return tooltipContent || badge.tooltipContent;
    }
    return badge.tooltipContent;
};
const ListingBadges = ({ listing, eventSubscriber, customBadges = [], badgesPerRow = 2, }) => {
    const [hoistedBadges, applicableBadges] = _.chain(availableBadges.concat(customBadges))
        .filter(klass => klass.shouldBeIncluded(listing, BADGE_MODE.EXPANDED))
        .sortBy('title')
        .sortBy('renderPriority')
        .partition(klass => klass.shouldBeHoisted)
        .value();
    return (_jsxs("section", { "data-elm-id": `listing-badges-${listing.listing_id}`, children: [hoistedBadges.map((Badge, i) => (_jsx(Badge, { listing: listing, eventSubscriber: eventSubscriber }, i))), _jsx(Grid, { columns: badgesPerRow, children: applicableBadges.map((Badge, i) => (_jsx(Grid.Column, { children: _jsx(Badge, { listing: listing, eventSubscriber: eventSubscriber }) }, i))) })] }));
};
ListingBadges.propTypes = {
    listing: PropTypes.object.isRequired,
    eventSubscriber: PropTypes.func,
    customBadges: PropTypes.array,
    badgesPerRow: PropTypes.number,
};
ListingBadges[GRAPH_API_FRAGMENT_DEFINITION] = `
  fragment ListingBadgesFields on Listing {
    listing_status
    listing_configuration {
      product_type
      broker_commission_message
      buyer_premium_available
      financing_available
      is_first_look_enabled
      first_look_end_date
      interior_access_allowed
      occupancy_status
      redemption_expiration_date
      trustee_sales_channel
      is_buyers_premium_available
    }

    listing_summary {
      is_remote_bid_enabled
      is_remote_before_and_during_auction_enabled
    }

    program_configuration {
      buyers_premium_minimum_amount
      buyers_premium_primary_amount
      buyers_premium_commission_type
    }

    primary_property {
      is_currently_saved
      is_newly_listed
    }

    open_houses {
      local_date
      start_time
      end_time
    }

    rental {
      current_rent
      lease_start_date
      lease_expiration_date
      is_security_deposit_charged
      security_deposit_amount
    }

    short_sale {
      agreement_expiration_date
    }

    event {
      event_code
      trustee_sale
    }

    parties {
      party_role
    }

    venue {
      venue_type
    }

    marketing_tags {
      tag
    }
  }
`;
ListingBadges[GRAPH_API_FRAGMENT_REFERENCE] = `...ListingBadgesFields`;
const getApplicableBadges = (listing, customBadges, maxBadges, priorityField = 'renderPriority', badgesList = availableBadges, badgeMode = BADGE_MODE.CONDENSED, shouldBeIncludedFn = 'shouldBeIncluded') => _.chain(badgesList.concat(customBadges))
    .filter(klass => klass[shouldBeIncludedFn](listing, badgeMode))
    .sortBy(priorityField)
    .slice(0, maxBadges);
const getV2ApplicableBadges = (listing, maxBadges = 3, customBadges = []) => getApplicableBadges(listing, customBadges, maxBadges, badge => badge.renderPriorityV2(listing), availableBadgesV2, BADGE_MODE.V2, 'shouldBeIncludedV2');
const ListingBadgesSummary = ({ listing, customBadges = [], maxBadges = 2, className, }) => {
    const applicableBadges = getApplicableBadges(listing, customBadges, maxBadges);
    return (_jsx("div", { "data-elm-id": `listing-badges-${listing.listing_id}`, className: cx(className, 'badge-summary'), children: applicableBadges.value().map((Badge, index) => (_jsx(Badge, { listing: listing, mode: BADGE_MODE.CONDENSED, className: cx('badge') }, index))) }));
};
ListingBadgesSummary[GRAPH_API_FRAGMENT_DEFINITION] =
    ListingBadges[GRAPH_API_FRAGMENT_DEFINITION];
ListingBadgesSummary[GRAPH_API_FRAGMENT_REFERENCE] =
    ListingBadges[GRAPH_API_FRAGMENT_REFERENCE];
ListingBadgesSummary.propTypes = {
    listing: PropTypes.object.isRequired,
    customBadges: PropTypes.array,
    maxBadges: PropTypes.number,
    className: PropTypes.string,
};
const ListingBadgesTags = ({ listing, customBadges = [], maxBadges = 3, className, isHorizontalView, viewingFrom = graph.enums.CurrentTargetAudience.Buyer, }) => {
    const applicableBadges = blueprint.listing.utils
        .calculatePropertyCardBadges(listing, viewingFrom)
        .slice(0, maxBadges)
        .map(badge => ({ title: badge.title, color: badge.color }));
    return (_jsx("div", { "data-elm-id": `listing-badges-${listing.listing_id}`, className: cx(className, 'badge-pill-container', {
            'badge-pill-map': isHorizontalView,
        }), children: applicableBadges.map(badge => (_jsx("span", { className: cx('badge-pill', badge.color ? badge.color : ''), children: badge.title }, `badge-${listing.listing_id}-${badge.title}`))) }));
};
ListingBadgesTags.propTypes = {
    listing: PropTypes.object.isRequired,
    customBadges: PropTypes.array,
    maxBadges: PropTypes.number,
    className: PropTypes.string,
    isHorizontalView: PropTypes.bool,
    viewingFrom: PropTypes.oneOf(Object.values(graph.enums.CurrentTargetAudience)),
};
ListingBadgesTags[GRAPH_API_FRAGMENT_DEFINITION] =
    ListingBadges[GRAPH_API_FRAGMENT_DEFINITION];
ListingBadgesTags[GRAPH_API_FRAGMENT_REFERENCE] =
    ListingBadges[GRAPH_API_FRAGMENT_REFERENCE];
export default ListingBadges;
export { ListingBadgesSummary, ListingBadge, ListingBadgesTags, getV2ApplicableBadges, getBadgeContent, getBadgeTooltip, };
//# sourceMappingURL=index.js.map